import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n/config';
import { translate } from 'hooks/useTranslate';

import { SelectorOption } from 'types';
import { TradingOfferOperationsCount } from 'types/Society';

const tradingOfferTypeOptionsRaw: {
  value: string;
  label: string;
  countKey: keyof TradingOfferOperationsCount;
}[] = [
  { value: 'BUY', label: 'Purchase', countKey: 'buy' },
  { value: 'SELL', label: 'Sale', countKey: 'sell' },
];

type UseGetTradingOfferTypeOptionsProps = {
  tradingOfferOperationsCount?: TradingOfferOperationsCount;
};

export const useGetTradingOfferTypeOptions = ({
  tradingOfferOperationsCount,
}: UseGetTradingOfferTypeOptionsProps) => {
  const [language, setLanguage] = useState(i18n.language);
  const [selectedTradingOfferTypeOption, setSelectedTradingOfferTypeOption] =
    useState<SelectorOption | null>(null);

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => setLanguage(lng);
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  const mapOption = useCallback(
    (option: { value: string; label: string }): SelectorOption => ({
      value: option.value,
      label: translate(option.label),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const tradingOfferTypeOptions = useMemo(
    () =>
      tradingOfferTypeOptionsRaw
        .filter(
          (option) => (tradingOfferOperationsCount?.[option.countKey] ?? 0) > 0
        )
        .map(mapOption),
    [mapOption, tradingOfferOperationsCount]
  );

  const handleChangeOption = (selectedOption: SelectorOption | null) => {
    setSelectedTradingOfferTypeOption(selectedOption);
  };

  return {
    data: {
      tradingOfferTypeOptions,
      selectedTradingOfferTypeOption,
    },
    actions: {
      handleChangeOption,
    },
  };
};
